import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaTrash, FaEdit, FaEye, FaChevronDown } from "react-icons/fa";
import clsx from "clsx";
import styles from "./index.module.css";
import { getAllTrancheDocument } from "../../service/api";
import { extractPaths } from "../../utils/extractPaths";
import ColumnDropdown from "./ColumnDropdown";
import { trancheAPISchema } from "../../yup";
import { showNotification } from "../../service/showNotification";
import TooltipWrapper from "../../helper/Tooltip";

const methodOptions = [
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
];

const getTabCountLabel = (count) => (count > 0 ? ` (${count})` : "");

const getFilledFieldsCount = (fields = []) =>
  fields.filter(
    (field) => field?.key?.toString().trim() && field?.value?.toString().trim()
  ).length;

const SimpleKeyValueTable = ({
  fields,
  control,
  name,
  onRemove,
  onAdd,
  addLabel = "+ Add",
}) => {
  return (
    <div className={styles.keyValueSection}>
      <div className={styles.paramsTableContainer}>
        <table className={styles.paramsTable}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th className={styles.actionsCol}></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr key={field.id}>
                <td>
                  <Controller
                    name={`${name}.${index}.key`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <input
                        {...controllerField}
                        type="text"
                        placeholder="Key"
                        className={styles.inputField}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </td>
                <td>
                  <Controller
                    name={`${name}.${index}.value`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <input
                        {...controllerField}
                        type="text"
                        placeholder="Value"
                        className={styles.inputField}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </td>
                <td className={styles.actionsCol}>
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className={styles.deleteBtn}
                  >
                    <FaTrash className={styles.deleteIcons} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={onAdd} className={styles.addHeaderBtn}>
        {addLabel}
      </button>
    </div>
  );
};

const BodyKeyValueTable = ({
  fields,
  control,
  name,
  onRemove,
  onAdd,
  responsePaths,
}) => {
  const [activeResponsePicker, setActiveResponsePicker] = useState(null);
  const responsePathOptions = responsePaths.map((pathItem) => ({
    value: `response.${pathItem.path}`,
    label: `response.${pathItem.path} (${pathItem.type})`,
  }));

  return (
    <div className={styles.keyValueSection}>
      <div className={styles.paramsTableContainer}>
        <table className={styles.paramsTable}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Value (Manual or Response Path)</th>
              <th className={styles.actionsCol}></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr key={field.id}>
                <td>
                  <Controller
                    name={`${name}.${index}.key`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <input
                        {...controllerField}
                        type="text"
                        placeholder="Key"
                        className={styles.inputField}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </td>
                <td>
                  <Controller
                    name={`${name}.${index}.value`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <>
                        <div className={styles.bodyValueRow}>
                          <input
                            {...controllerField}
                            type="text"
                            placeholder="Value"
                            className={styles.inputField}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                              }
                            }}
                          />
                          {responsePaths.length > 0 && (
                            <TooltipWrapper tooltip="Add API response">
                              <button
                                type="button"
                                className={styles.responsePickerButton}
                                onClick={() => setActiveResponsePicker(index)}
                                aria-label="Select response path"
                              >
                                <FaChevronDown />
                              </button>
                            </TooltipWrapper>
                          )}
                        </div>
                      </>
                    )}
                  />
                </td>
                <td className={styles.actionsCol}>
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className={styles.deleteBtn}
                  >
                    <FaTrash className={styles.deleteIcons} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={onAdd} className={styles.addHeaderBtn}>
        + Add
      </button>
      {activeResponsePicker !== null && responsePaths.length > 0 && (
        <div
          className={styles.responsePickerOverlay}
          onClick={() => setActiveResponsePicker(null)}
        >
          <div
            className={styles.responsePickerModal}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.responsePickerHeader}>
              <h5>Select Response Path</h5>
              <button
                type="button"
                className={styles.responsePickerClose}
                onClick={() => setActiveResponsePicker(null)}
                aria-label="Close response path picker"
              >
                ×
              </button>
            </div>
            <p className={styles.responsePickerHelp}>
              Choose a response path to populate this body value.
            </p>
            <Controller
              name={`${name}.${activeResponsePicker}.value`}
              control={control}
              render={({ field: controllerField }) => (
                <Select
                  classNamePrefix="trancheResponseSelect"
                  options={responsePathOptions}
                  placeholder="Select response path..."
                  onChange={(option) => {
                    controllerField.onChange(option?.value || "");
                    if (option) {
                      setActiveResponsePicker(null);
                    }
                  }}
                  isClearable
                  value={
                    responsePathOptions.find(
                      (option) => option.value === controllerField.value
                    ) || null
                  }
                  maxMenuHeight={220}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Component to display API endpoint details in a modal
const EndpointDetailsModal = ({ show, onHide, endpoint }) => {
  if (!endpoint) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>API Endpoint Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.endpointDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{endpoint.name || "N/A"}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Description:</strong>
            <span>{endpoint.description || "N/A"}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Method:</strong>
            <span className={styles.methodBadge}>{endpoint.method}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>URL:</strong>
            <span className={styles.urlText}>{endpoint.url}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Zoho Key:</strong>
            <span>{endpoint.zohoKey || "N/A"}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Is Zoho API:</strong>
            <span>{endpoint.isZohoAPI ? "Yes" : "No"}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Is Active:</strong>
            <span>{endpoint.isActive ? "Yes" : "No"}</span>
          </div>

          {endpoint.headers && Object.keys(endpoint.headers).length > 0 && (
            <div className={styles.detailSection}>
              <strong>Headers:</strong>
              <pre className={styles.codeBlock}>
                {JSON.stringify(endpoint.headers, null, 2)}
              </pre>
            </div>
          )}

          {endpoint.params && Object.keys(endpoint.params).length > 0 && (
            <div className={styles.detailSection}>
              <strong>Params:</strong>
              <pre className={styles.codeBlock}>
                {JSON.stringify(endpoint.params, null, 2)}
              </pre>
            </div>
          )}

          {endpoint.response && (
            <div className={styles.detailSection}>
              <strong>Response:</strong>
              <pre className={styles.codeBlock}>
                {JSON.stringify(endpoint.response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const TrancheApiForm = ({
  apiData,
  apiEndpoints,
  onSubmit,
  onCancel,
  onFetchApiEndpoint,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [responsePaths, setResponsePaths] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("body");
  const [isEditingName, setIsEditingName] = useState(false);
  const isEditing = !!apiData;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(trancheAPISchema),
    defaultValues: {
      name: "",
      url: "",
      method: "POST",
      apiEndpointId: null,
      conditionalColumnIds: [],
      headers: [{ key: "", value: "" }],
      params: [{ key: "", value: "" }],
      body: [{ key: "", value: "" }],
    },
  });

  const watchedName = useWatch({ control, name: "name" });
  const watchedHeaders = useWatch({ control, name: "headers" }) || [];
  const watchedParams = useWatch({ control, name: "params" }) || [];
  const watchedBody = useWatch({ control, name: "body" }) || [];
  const watchedConditions =
    useWatch({ control, name: "conditionalColumnIds" }) || [];

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
  } = useFieldArray({ control, name: "headers" });

  const {
    fields: paramFields,
    append: appendParam,
    remove: removeParam,
  } = useFieldArray({ control, name: "params" });

  const {
    fields: bodyFields,
    append: appendBody,
    remove: removeBody,
  } = useFieldArray({ control, name: "body" });

  useEffect(() => {
    if (apiData) {
      const convertToArray = (obj) => {
        if (!obj || typeof obj !== "object") return [{ key: "", value: "" }];
        const entries = Object.entries(obj);
        return entries.length > 0
          ? entries.map(([key, value]) => ({ key, value: String(value) }))
          : [{ key: "", value: "" }];
      };

      // Use conditionalColumnIds array directly
      const formConditionals = Array.isArray(apiData.conditionalColumnIds)
        ? apiData.conditionalColumnIds
        : [];

      reset({
        name: apiData.name || "",
        url: apiData.url || "",
        method: apiData.method || "POST",
        apiEndpointId: apiData.apiEndPoint?._id || apiData.apiEndPoint || apiData.apiEndpointId?._id || apiData.apiEndpointId || null,
        conditionalColumnIds: formConditionals,
        headers: convertToArray(apiData.headers),
        params: convertToArray(apiData.params),
        body: convertToArray(apiData.body),
      });
    } else {
      reset({
        name: "",
        url: "",
        method: "POST",
        apiEndpointId: null,
        conditionalColumnIds: [],
        headers: [{ key: "", value: "" }],
        params: [{ key: "", value: "" }],
        body: [{ key: "", value: "" }],
      });
    }
  }, [apiData, reset]);

  // --- Effect 1: When editing an existing API, fetch endpoint details from apiData directly ---
  useEffect(() => {
    if (!apiData || !onFetchApiEndpoint) {
      if (!apiData) {
        setSelectedEndpoint(null);
        setResponsePaths([]);
      }
      return;
    }

    const endpointId = apiData.apiEndPoint?._id || apiData.apiEndPoint || apiData.apiEndpointId?._id || apiData.apiEndpointId;
    if (!endpointId) return;

    (async () => {
      try {
        const res = await onFetchApiEndpoint(endpointId);
        const fullEndpoint = res?.data || res;
        if (fullEndpoint) {
          setSelectedEndpoint(fullEndpoint);
          if (fullEndpoint?.response?.body) {
            setResponsePaths(extractPaths(fullEndpoint.response.body));
          } else {
            setResponsePaths([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch endpoint on edit:", err);
        showNotification("error", "Failed to fetch API endpoint details");
      }
    })();
  }, [apiData, onFetchApiEndpoint]);

  // --- Effect 2: When user manually selects a new endpoint from the dropdown ---
  const apiEndpointId = useWatch({ control, name: "apiEndpointId" });

  useEffect(() => {
    let watchId = apiEndpointId;
    if (watchId && typeof watchId === 'object' && watchId._id) {
      watchId = watchId._id;
    }

    if (!watchId) {
      setSelectedEndpoint(null);
      setResponsePaths([]);
      return;
    }

    // Skip if this matches the original apiData endpoint — Effect 1 already handled that initial load
    const originalId = apiData?.apiEndPoint?._id || apiData?.apiEndPoint || apiData?.apiEndpointId?._id || apiData?.apiEndpointId;
    if (originalId && String(watchId) === String(originalId)) return;

    if (!onFetchApiEndpoint) return;

    (async () => {
      try {
        const res = await onFetchApiEndpoint(watchId);
        const fullEndpoint = res?.data || res;
        if (fullEndpoint) {
          setSelectedEndpoint(fullEndpoint);
          if (fullEndpoint?.response?.body) {
            setResponsePaths(extractPaths(fullEndpoint.response.body));
          } else {
            setResponsePaths([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch endpoint details:", err);
        showNotification("error", "Failed to fetch API endpoint details");
      }
    })();
  }, [apiEndpointId, onFetchApiEndpoint, apiData]);


  const handleFormSubmit = async (data) => {
    console.log("Form submitted with data:", data);
    setIsLoading(true);
    try {
      const convertToObject = (arr) => {
        return arr.reduce((acc, { key, value }) => {
          if (key) acc[key] = value;
          return acc;
        }, {});
      };

      // Extract column IDs from conditionalColumnIds array
      const columnIds = Array.isArray(data.conditionalColumnIds)
        ? data.conditionalColumnIds.map(col => typeof col === 'object' ? col._id : col)
        : [];

      const payload = {
        name: data.name,
        url: data.url,
        method: data.method,
        headers: convertToObject(data.headers),
        params: convertToObject(data.params),
        body: convertToObject(data.body),
        isDocument: false,
        apiEndPoint: data.apiEndpointId,
        conditionalColumnIds: columnIds,
      };
      console.log("Payload being sent:", payload);
      await onSubmit(payload);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvalidSubmit = (formErrors) => {
    if (formErrors?.name) {
      setIsEditingName(true);
    }
  };

  const apiEndpointOptions = (apiEndpoints || []).map((endpoint) => ({
    value: endpoint._id,
    label: endpoint.name || endpoint.url || endpoint._id,
  }));

  const renderTabButton = (tabKey, label, count, extraClassName) => (
    <button
      type="button"
      onClick={() => setActiveTab(tabKey)}
      className={clsx(
        styles.tabButton,
        activeTab === tabKey && styles.activeTab,
        extraClassName
      )}
    >
      {label}
      {getTabCountLabel(count)}
    </button>
  );

  return (
    <div className={styles.appContainer}>
      <div className={styles.header}>
        {isEditingName ? (
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <input
                  {...field}
                  className={clsx(styles.nameInput, error && styles.inputError)}
                  autoFocus
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setIsEditingName(false);
                    }
                  }}
                />
                <span className={styles.errorMessage}>{error?.message}</span>
              </div>
            )}
          />
        ) : (
          <div className={styles.headerName}>
            <h4>{watchedName || "Untitled Request"}</h4>
            <button
              type="button"
              onClick={() => setIsEditingName(true)}
              className={styles.editIconBtn}
              aria-label="Edit request name"
            >
              <FaEdit className={styles.editIcon} />
            </button>
          </div>
        )}
        {!isEditingName && (
          <span className={styles.errorMessage}>{errors.name?.message || ""}</span>
        )}
      </div>

      <Form onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}>
        <div className={styles.requestUrlSection}>
          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <Select
                className={styles.methodSelect}
                classNamePrefix="trancheMethodSelect"
                options={methodOptions}
                value={methodOptions.find((opt) => opt.value === field.value)}
                onChange={(opt) => field.onChange(opt?.value)}
              />
            )}
          />
          <Controller
            name="url"
            control={control}
            render={({ field }) => (
              <div className={styles.urlInputContainer}>
                <input
                  {...field}
                  type="text"
                  placeholder="https://api.example.com/endpoint"
                  className={clsx(styles.urlInput, errors.url && styles.inputError)}
                />
                <span className={styles.errorMessage}>
                  {errors.url?.message || ""}
                </span>
              </div>
            )}
          />
        </div>

        <div className={clsx(styles.tabContent, styles.formSetupSection)}>
          <Form.Group className="mb-3">
            <Form.Label>API Endpoint</Form.Label>
            <div className={styles.endpointSelectWrapper}>
              <Controller
                name="apiEndpointId"
                control={control}
                render={({ field }) => {
                  const fieldValue =
                    field.value && typeof field.value === "object"
                      ? field.value._id
                      : field.value;
                  const selectedOption = apiEndpointOptions.find(
                    (opt) => String(opt.value) === String(fieldValue)
                  );

                  return (
                    <Select
                      className={clsx(
                        styles.endpointSelect,
                        errors.apiEndpointId && styles.invalidSelect
                      )}
                      options={apiEndpointOptions}
                      placeholder="Select an API endpoint..."
                      value={
                        selectedOption ||
                        (field.value && typeof field.value === "object"
                          ? {
                              value: field.value._id,
                              label: field.value.name || field.value._id,
                            }
                          : null)
                      }
                      onChange={(opt) => field.onChange(opt?.value || null)}
                      isClearable
                    />
                  );
                }}
              />
              {selectedEndpoint && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className={styles.viewDetailsBtn}
                  onClick={() => setShowDetailsModal(true)}
                  title="View Endpoint Details"
                >
                  <FaEye />
                </Button>
              )}
            </div>
            {errors.apiEndpointId && (
              <span className={styles.errorMessage}>
                {errors.apiEndpointId?.message}
              </span>
            )}
            <Form.Text className="text-muted">
              Select an API endpoint to auto-populate its response structure for body mapping
            </Form.Text>
            {(selectedEndpoint || apiEndpointId) && (
              <div className={styles.documentSection}>
                <p className={styles.endpointNoteText}>
                  <strong>Note:</strong> Values starting with <code>response</code> in the Body will automatically use the corresponding value from the API endpoint response.
                </p>
              </div>
            )}
          </Form.Group>

        </div>

        <div className={styles.tabsContainer}>
          {renderTabButton("params", "Params", getFilledFieldsCount(watchedParams))}
          {renderTabButton("headers", "Headers", getFilledFieldsCount(watchedHeaders))}
          {renderTabButton("body", "Body", getFilledFieldsCount(watchedBody))}
          {renderTabButton(
            "conditions",
            "Conditions",
            Array.isArray(watchedConditions) ? watchedConditions.length : 0,
            errors?.conditionalColumnIds && styles.tabError
          )}
        </div>

        <div className={styles.tabContent}>
          {activeTab === "params" && (
            <SimpleKeyValueTable
              fields={paramFields}
              control={control}
              name="params"
              onRemove={removeParam}
              onAdd={() => appendParam({ key: "", value: "" })}
              addLabel="+ Add"
            />
          )}
          {activeTab === "headers" && (
            <SimpleKeyValueTable
              fields={headerFields}
              control={control}
              name="headers"
              onRemove={removeHeader}
              onAdd={() => appendHeader({ key: "", value: "" })}
              addLabel="+ Add header"
            />
          )}
          {activeTab === "body" && (
            <BodyKeyValueTable
              fields={bodyFields}
              control={control}
              name="body"
              onRemove={removeBody}
              onAdd={() => appendBody({ key: "", value: "" })}
              responsePaths={responsePaths}
            />
          )}
          {activeTab === "conditions" && (
            <div className={styles.conditionsTabContent}>
              <ColumnDropdown
                name="conditionalColumnIds"
                control={control}
                placeholder="Search conditional columns..."
                fetchOptions={getAllTrancheDocument}
              />
              <span className={styles.errorMessage}>
                {errors?.conditionalColumnIds?.message || ""}
              </span>
            </div>
          )}
        </div>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading
              ? isEditing
                ? "Updating..."
                : "Creating..."
              : isEditing
                ? "Update API"
                : "Create API"}
          </Button>
        </div>

        <EndpointDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          endpoint={selectedEndpoint}
        />
      </Form>
    </div>
  );
};

export default TrancheApiForm;
